import {
    collection,
    getVariantByParentType,
    globalCounterPath,
    parentTypeFor,
    readGlobalCount,
} from "../_variants/index.js";

import { pad } from "../../../utils";

const isEADT = ({ liveInputData }) => {
    return ["EADT"].includes(liveInputData.Type);
};

// --- Constants ---

export const DESIGN_STANDARDS = [
    { "label": "European Technical Standard Order (ETSO)", "id": "ETSO" },
    { "label": "Technical Standard Order (TSO)", "id": "TSO" },
    { "label": "Military Specification (MS)", "id": "MS" },
    { "label": "British Standard (BSi)", "id": "BSi" },
    { "label": "European Standard (EN)", "id": "EN" },
    { "label": "International Organization for Standardization (ISO)", "id": "ISO" },
    { "label": "Underwriters Laboratories (UL)", "id": "UL" },
];

export const ENV_CONDITIONS = [
    { "label": "Temperature & Altitude", "id": "TemperatureAltitude" },
    { "label": "Low Temperature", "id": "LowTemperature" },
    { "label": "High Temperature", "id": "HighTemperature" },
    { "label": "In-Flight Loss of Cooling", "id": "InFlightLossOfCooling" },
    { "label": "Altitude", "id": "Altitude" },
    { "label": "Decompression", "id": "Decompression" },
    { "label": "Overpressure", "id": "Overpressure" },
    { "label": "Temperature Variation", "id": "TemperatureVariation" },
    { "label": "Humidity", "id": "Humidity" },
    { "label": "Operational Shock & Crash Safety", "id": "OperationalShockCrashSafety" },
    { "label": "Vibration", "id": "Vibration" },
    { "label": "Explosive Atmosphere", "id": "ExplosiveAtmosphere" },
    { "label": "Waterproofness", "id": "Waterproofness" },
    { "label": "Fluids Susceptibility", "id": "FluidsSusceptibility" },
    { "label": "Sand and Dust", "id": "SandAndDust" },
    { "label": "Fungus", "id": "Fungus" },
    { "label": "Salt Fog Test", "id": "SaltFogTest" },
    { "label": "Magnetic Effect", "id": "MagneticEffect" },
    { "label": "Power Input", "id": "PowerInput" },
    { "label": "Voltage Spike", "id": "VoltageSpike" },
    { "label": "Audio Frequency Susceptibility", "id": "AudioFrequencySusceptibility" },
    { "label": "Induced Signal Susceptibility", "id": "InducedSignalSusceptibility" },
    { "label": "Radio Frequency Susceptibility", "id": "RadioFrequencySusceptibility" },
    { "label": "Radio Frequency Emission", "id": "RadioFrequencyEmission" },
    { "label": "Lightning Induced Transient Susceptibility", "id": "LightningInducedTransientSusceptibility" },
    { "label": "Lightning Direct Effects", "id": "LightningDirectEffects" },
    { "label": "Icing", "id": "Icing" },
    { "label": "Electrostatic Discharge", "id": "ElectrostaticDischarge" },
    { "label": "Fire / Flammability", "id": "FireFlammability" },
    { "label": "Other Tests", "id": "OtherTests" },
];

const YES_NO = [
    { "label": "Yes", "id": "Yes" },
    { "label": "No", "id": "No" },
];

// --- Helpers ---

export function computeClassification(liveInputData) {
    const fc = liveInputData.FailureClassification;
    if (!fc) return null;
    if (fc === "NoSafetyEffect" || fc === "Minor") return "Simplified";
    if (fc === "Hazardous" || fc === "Catastrophic") return "Detailed";
    // fc === "Major" — follow-up questions
    const q2 = liveInputData.ControlsAircraft;
    if (q2 === "Yes") return "Detailed";
    if (q2 !== "No") return null;
    const q3 = liveInputData.EquipmentComplex;
    if (q3 === "Yes") return "Detailed";
    if (q3 !== "No") return null;
    const q4 = liveInputData.SimpleAssessment;
    if (q4 === "Yes") return "Simplified";
    if (q4 === "No") return "Detailed";
    return null;
}

export const sectionAFormFields = [{
    "label": "Title",
    "name": "Title",
    "type": "text",
},{
    "label": "Item Name",
    "name": "Name",
    "type": "text",
    "formStep": 1,
}, {
    "label": "Part Number",
    "name": "PartNumberID",
    "type": "dropdown",
    "dataLocation": "Database/PartNumbers",
    "idKey": "ID",
    "labelKey": "Number",
    "formStep": 1,
}, {
    "label": "Description",
    "name": "Description",
    "divClass": "md:col-span-3",
    "type": "richtext",
    "formStep": 1,
}];

// --- Display fields for EADT detail page ---

export const sectionAFields = [{
    "label": "Manufacturer",
    "key": "Manufacturer",
    "grow": true,
    "link": "Database/Manufacturers/:ManufacturerID"
}, {
    "label": "Item Name",
    "key": "Name",
    "grow": true
}, {
    "label": "Description",
    "key": "Description",
    "grow": true,
    "class": "md:col-span-3"
}];

export const simplifiedFields = [{
    "label": "Qualification",
    "key": "Qualification",
    "grow": true,
    "class": "md:col-span-3"
}, {
    "label": "Equipment Specifications / Operating Limitations",
    "key": "SpecsLimitations",
    "grow": true,
    "class": "md:col-span-3"
}, {
    "label": "Documentation",
    "key": "Documentation",
    "grow": true,
    "class": "md:col-span-3"
}, {
    "label": "Additional Data",
    "key": "AdditionalData",
    "grow": true,
    "class": "md:col-span-3"
}, {
    "label": "Required Maintenance",
    "key": "RequiredMaintenance",
    "grow": true,
    "class": "md:col-span-3"
}, {
    "label": "Suitability for Proposed Application",
    "key": "Suitability",
    "grow": true,
    "class": "md:col-span-3"
}];

export const detailedFields = [{
    "label": "Design Standards",
    "key": "DesignStandards",
    "grow": true,
    "class": "md:col-span-3"
}, {
    "label": "Additional Data",
    "key": "AdditionalData",
    "grow": true,
    "class": "md:col-span-3"
}, {
    "label": "Required Maintenance",
    "key": "RequiredMaintenance",
    "grow": true,
    "class": "md:col-span-3"
}, {
    "label": "Suitability for Proposed Application",
    "key": "Suitability",
    "grow": true,
    "class": "md:col-span-3"
}];

export const limitationFields = [{
    "label": "Limitation L1",
    "key": "LimitationL1",
    "grow": true,
    "class": "md:col-span-3"
}, {
    "label": "Limitation L2",
    "key": "LimitationL2",
    "grow": true,
    "class": "md:col-span-3"
}, {
    "label": "Limitation L3",
    "key": "LimitationL3",
    "grow": true,
    "class": "md:col-span-3"
}];

export function addEADT({ form, pageItem, store, db, meta }) {
    const variant = getVariantByParentType(meta?.ParentType ?? pageItem?.ParentType);
    const eadtCollection = collection(variant, "EADTs");
    const eadtCollectionTail = parentTypeFor(variant, "EADTs");
    const earCollection = collection(variant, "EARs");
    const eadtCounterPath = globalCounterPath(variant, "counts/eadt/count");

    const nextNumber = readGlobalCount(store, variant, ["eadt"]);
    const number = `${variant.docPrefix}EADT-${pad(nextNumber)}`
    form.setConfig({
        "title": "Add EADT",
        "location": `${eadtCollection}/`,
        "type": "push",
        "isSteppedForm": true,
        "description": "EADT's are taken out first then all the EADT data can be inputted once created.",
        "fields": [{
            "label": "EADT Number",
            "type": "value",
            value() {
                return number;
            }
        }, {
            "label": "Title",
            "name": "Name",
            "type": "text",
            "divClass": "md:col-span-2"
        }, {
            "label": "Part Number",
            "name": "PartNumberID",
            "type": "dropdown",
            "dataLocation": "Database/PartNumbers",
            "idKey": "ID",
            "labelKey": "Number",
            "required": false
        }, {
            "label": "Description",
            "name": "Description",
            "type": "text",
            "divClass": "md:col-span-3"
        }, {
            "label": "FHA / Depth of Analysis (see AMC 25.1309 for guidance)",
            "type": "sectionHeader",
            "divClass": "md:col-span-3"
        }, {
            "label": "Per the Equipment Assessment, what is the Failure Classification of the equipment?",
            "name": "FailureClassification",
            "type": "dropdown",
            "divClass": "md:col-span-3",
            "dropdownData": [
                { "label": "No Safety Effect", "id": "NoSafetyEffect" },
                { "label": "Minor", "id": "Minor" },
                { "label": "Major", "id": "Major" },
                { "label": "Hazardous", "id": "Hazardous" },
                { "label": "Catastrophic", "id": "Catastrophic" }
            ]
        }, {
            "label": "Is the Equipment to be used in a system that controls the aircraft?",
            "name": "ControlsAircraft",
            "type": "dropdown",
            "divClass": "md:col-span-3",
            "dropdownData": [
                { "label": "Yes", "id": "Yes" },
                { "label": "No", "id": "No" }
            ],
            show({ liveInputData }) {
                return liveInputData.FailureClassification === "Major";
            }
        }, {
            "label": "Is the equipment Complex?",
            "name": "EquipmentComplex",
            "type": "dropdown",
            "divClass": "md:col-span-3",
            "dropdownData": [
                { "label": "Yes", "id": "Yes" },
                { "label": "No", "id": "No" }
            ],
            show({ liveInputData }) {
                return liveInputData.FailureClassification === "Major" && liveInputData.ControlsAircraft === "No";
            }
        }, {
            "label": "Can the equipment be assessed with a simple EADT, based on experience, judgement or other definable justifications?",
            "name": "SimpleAssessment",
            "type": "dropdown",
            "divClass": "md:col-span-3",
            "dropdownData": [
                { "label": "Yes", "id": "Yes" },
                { "label": "No", "id": "No" }
            ],
            show({ liveInputData }) {
                return liveInputData.FailureClassification === "Major" && liveInputData.ControlsAircraft === "No" && liveInputData.EquipmentComplex === "No";
            }
        }, {
            "label": "EADT Classification",
            "type": "value",
            "divClass": "md:col-span-3",
            value({ liveInputData }) {
                return computeClassification(liveInputData) || "";
            },
            show({ liveInputData }) {
                return computeClassification(liveInputData) !== null;
            }
        }],
        async preSubmitHook({ liveInputData, dropdownData, db }) {
            let pn = liveInputData.PartNumberID && dropdownData.PartNumberID
                ? dropdownData.PartNumberID.find(i => i.id === liveInputData.PartNumberID)
                : null;
            const claimed = await db.utils.allocateCount(eadtCounterPath);
            const number = `${variant.docPrefix}EADT-${pad(claimed)}`;
            return {
                "Cancelled": false,
                "CreatedBy": store.getters.userName,
                "ParentID": pageItem.ID,
                "ParentType": parentTypeFor(variant, "EARs"),
                "Parent": pageItem.Number,
                "PartNumberID": liveInputData.PartNumberID || null,
                "PartNumber": pn ? pn.label : null,
                "ManufacturerID": pn?.meta?.ManufacturerID || null,
                "Manufacturer": pn?.meta?.Manufacturer || null,
                "PartNumbers": pn ? { [liveInputData.PartNumberID]: true } : null,
                "Date": {
                    "Created": new Date().toISOString(),
                    "Cancelled": "N/A",
                    "Signedoff": "N/A"
                },
                "Revision": 1,
                "RevisionID": "---R1",
                "Revisions": {
                    "---R1": {
                        "Revision": 1,
                        "Reason": "EADT Created",
                        "ID": "---R1",
                        "Date": new Date().toISOString()
                    }
                },
                "Number": number,
                "EADTClassification": computeClassification(liveInputData),
                "FailureClassification": liveInputData.FailureClassification || null,
                "ControlsAircraft": liveInputData.ControlsAircraft || null,
                "EquipmentComplex": liveInputData.EquipmentComplex || null,
                "SimpleAssessment": liveInputData.SimpleAssessment || null,
            };

        },
        async postSubmitHook({ newData, liveInputData, dropdownData }) {
            await db.set(`${earCollection}/${pageItem.ID}/${eadtCollectionTail}/${newData.ID}`, true);
            await db.set(`Database/${pageItem.ParentType}/${pageItem.ParentID}/${eadtCollectionTail}/${newData.ID}`, true);
            if (liveInputData.PartNumberID && dropdownData.PartNumberID) {
                let pn = dropdownData.PartNumberID.find(i => i.id === liveInputData.PartNumberID);
                if (pn) {
                    await db.update(`Database/PartNumbers/${pn.id}`, { "Documents": { [newData.ID]: true }, [eadtCollectionTail]: { [newData.ID]: true } });
                    if (pn.meta?.ManufacturerID) {
                        await db.set(`Database/Manufacturers/${pn.meta.ManufacturerID}/${eadtCollectionTail}/${newData.ID}`, true);
                    }
                }
            }
        }
    });
    form.getDropdownData();
    form.toggle();
}


// --- Section A: Edit core EADT data (Item Name, Part Number, Description, Datasheet) ---

export function editEadtData({ item, db, form, meta, pageItem }) {
    const variant = getVariantByParentType(meta?.ParentType ?? item?.ParentType ?? pageItem?.ParentType);
    const eadtCollection = collection(variant, "EADTs");
    const eadtCollectionTail = parentTypeFor(variant, "EADTs");
    const docCollection = collection(variant, "Documents");
    form.setValues({
        ...item,
        "Datasheet": null
    });
    form.setConfig({
        "title": "Edit Main EADT Data",
        "location": `${eadtCollection}/${item.ID}/`,
        "type": "update",
        "isSteppedForm": false,
        "fields": [...sectionAFormFields, {
            "label": "FHA / Depth of Analysis (see AMC 25.1309 for guidance)",
            "type": "sectionHeader",
            "divClass": "md:col-span-3"
        }, {
                "label": "Per the Equipment Assessment, what is the Failure Classification of the equipment?",
                "name": "FailureClassification",
                "type": "dropdown",
                "divClass": "md:col-span-3",
                "dropdownData": [
                    { "label": "No Safety Effect", "id": "NoSafetyEffect" },
                    { "label": "Minor", "id": "Minor" },
                    { "label": "Major", "id": "Major" },
                    { "label": "Hazardous", "id": "Hazardous" },
                    { "label": "Catastrophic", "id": "Catastrophic" }
                ]
            }, {
                "label": "Is the Equipment to be used in a system that controls the aircraft?",
                "name": "ControlsAircraft",
                "type": "dropdown",
                "divClass": "md:col-span-3",
                "dropdownData": [
                    { "label": "Yes", "id": "Yes" },
                    { "label": "No", "id": "No" }
                ],
                show({ liveInputData }) {
                    return liveInputData.FailureClassification === "Major";
                }
            }, {
                "label": "Is the equipment Complex?",
                "name": "EquipmentComplex",
                "type": "dropdown",
                "divClass": "md:col-span-3",
                "dropdownData": [
                    { "label": "Yes", "id": "Yes" },
                    { "label": "No", "id": "No" }
                ],
                show({ liveInputData }) {
                    return liveInputData.FailureClassification === "Major" && liveInputData.ControlsAircraft === "No";
                }
            }, {
                "label": "Can the equipment be assessed with a simple EADT, based on experience, judgement or other definable justifications?",
                "name": "SimpleAssessment",
                "type": "dropdown",
                "divClass": "md:col-span-3",
                "dropdownData": [
                    { "label": "Yes", "id": "Yes" },
                    { "label": "No", "id": "No" }
                ],
                show({ liveInputData }) {
                    return liveInputData.FailureClassification === "Major" && liveInputData.ControlsAircraft === "No" && liveInputData.EquipmentComplex === "No";
                }
            }, {
                "label": "EADT Classification",
                "type": "value",
                "divClass": "md:col-span-3",
                value({ liveInputData }) {
                    return computeClassification(liveInputData) || "";
                },
                show({ liveInputData }) {
                    return computeClassification(liveInputData) !== null;
                }
            }],
        async onStepChange({ files }) {
            if (files.length > 0 && files[0].length < 1) return;
            for (const file of files) {
                await db.upload(`${docCollection}/${item.ID}/Datasheet`, file[0]);
                await db.push(`${eadtCollection}/${item.ID}/Datasheets`, {
                    "File": file[0].name
                });
            }
        },
        async preSubmitHook({ liveInputData, dropdownData }) {
            let pn = dropdownData.PartNumberID.find(i => i.id === liveInputData.PartNumberID);
            await db.update(`Database/PartNumbers/${pn.id}`, {
                "Documents": {
                    [item.ID]: true
                }
            });
            return {
                "PartNumber": pn.label,
                "ManufacturerID": pn?.meta?.ManufacturerID || null,
                "Manufacturer": pn?.meta?.Manufacturer || null,
                "PartNumbers": {
                    [liveInputData.PartNumberID]: true
                },
                "EADTClassification": computeClassification(liveInputData),
                "FailureClassification": liveInputData.FailureClassification || null,
                "ControlsAircraft": liveInputData.ControlsAircraft || null,
                "EquipmentComplex": liveInputData.EquipmentComplex || null,
                "SimpleAssessment": liveInputData.SimpleAssessment || null,
            }
        },
        async postSubmitHook({ liveInputData, dropdownData, db }) {
            let pn = dropdownData.PartNumberID.find(i => i.id === liveInputData.PartNumberID);
            await db.update(`Database/PartNumbers/${pn.id}`, {
                [eadtCollectionTail]: {
                    [item.ID]: true
                }
            });
            await db.set(`Database/Manufacturers/${pn.meta.ManufacturerID}/${eadtCollectionTail}/${item.ID}`, true);
        }
    });
    form.getDropdownData();
    form.toggle();
}

// --- Section B: Simplified EADT ---

export const editSimplifiedEADT = ({ item, form, meta, pageItem }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? item?.ParentType ?? pageItem?.ParentType);
    const eadtCollection = collection(variant, "EADTs");
    form.setValues(item);
    form.setConfig({
        "title": "Edit Simplified EADT (Section B)",
        "location": `${eadtCollection}/${item.ID}/`,
        "type": "update",
        "isSteppedForm": true,
        "fields": [{
            "label": "Equipment Design Standard / Qualification",
            "name": "Qualification",
            "divClass": "md:col-span-3",
            "type": "richtext",
            "formStep": 1
        }, {
            "label": "Equipment Specifications / Operating Limitations",
            "name": "SpecsLimitations",
            "divClass": "md:col-span-3",
            "type": "richtext",
            "formStep": 1
        }, {
            "label": "Documentation",
            "name": "Documentation",
            "divClass": "md:col-span-3",
            "type": "richtext",
            "formStep": 1
        }, {
            "label": "Additional Data",
            "name": "AdditionalData",
            "divClass": "md:col-span-3",
            "type": "richtext",
            "formStep": 2
        }, {
            "label": "Required Maintenance",
            "name": "RequiredMaintenance",
            "divClass": "md:col-span-3",
            "type": "richtext",
            "formStep": 2
        }, {
            "label": "Suitability for Proposed Application",
            "name": "Suitability",
            "divClass": "md:col-span-3",
            "type": "richtext",
            "formStep": 2
        }]
    });
    form.toggle();
};

// --- Section C: Detailed EADT Actions ---

export const editDesignStandards = ({ item, form, meta, pageItem }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? item?.ParentType ?? pageItem?.ParentType);
    const eadtCollection = collection(variant, "EADTs");
    form.setValues(item);
    form.setConfig({
        "title": "Edit Design Standards",
        "location": `${eadtCollection}/${item.ID}/`,
        "type": "update",
        "fields": [{
            "label": "Equipment Design Standard / Qualification",
            "name": "DesignStandards",
            "type": "dropdown",
            "divClass": "md:col-span-3",
            "multiple": true,
            "hasOther": true,
            "dropdownData": DESIGN_STANDARDS
        }]
    });
    form.toggle();
};

export const editEquipmentSpecs = ({ item, form, meta, pageItem }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? item?.ParentType ?? pageItem?.ParentType);
    const eadtCollection = collection(variant, "EADTs");
    form.setValues(item);
    form.setConfig({
        "title": "Edit Equipment Specifications / Operating Limitations",
        "location": `${eadtCollection}/${item.ID}/`,
        "type": "update",
        "fields": [{
            "label": "Electrical Specifications",
            "name": "Specs[Electrical]",
            "type": "text",
            "divClass": "md:col-span-3"
        }, {
            "label": "Mechanical Specifications",
            "name": "Specs[Mechanical]",
            "type": "text",
            "divClass": "md:col-span-3"
        }, {
            "label": "Installation Manual",
            "name": "Specs[InstallationManual]",
            "type": "text",
            "divClass": "md:col-span-3"
        }, {
            "label": "DDP Ref.",
            "name": "Specs[DDPRef]",
            "type": "text",
            "divClass": "md:col-span-3"
        }, {
            "label": "Test Reports in Lieu of DDP",
            "name": "Specs[TestReportsLieuDDP]",
            "type": "text",
            "divClass": "md:col-span-3"
        }, {
            "label": "Other documents in lieu of test reports",
            "name": "Specs[OtherDocsLieuTestReports]",
            "type": "text",
            "divClass": "md:col-span-3"
        }, {
            "label": "Reference documents / drawings",
            "name": "Specs[ReferenceDocsDrawings]",
            "type": "text",
            "divClass": "md:col-span-3"
        }, {
            "label": "Limitations from above",
            "name": "Specs[LimitationsFromAbove]",
            "type": "text",
            "divClass": "md:col-span-3"
        }]
    });
    form.toggle();
};

export const editSoftwareAssessment = ({ item, form, meta, pageItem }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? item?.ParentType ?? pageItem?.ParentType);
    const eadtCollection = collection(variant, "EADTs");
    form.setValues(item);
    form.setConfig({
        "title": "Edit Software Assessment",
        "location": `${eadtCollection}/${item.ID}/`,
        "type": "update",
        "fields": [{
            "label": "Software Assessment",
            "type": "sectionHeader",
            "divClass": "md:col-span-3"
        }, {
            "label": "1. Does the equipment contain software?",
            "name": "Software[ContainsSoftware]",
            "type": "dropdown",
            "divClass": "md:col-span-3",
            "dropdownData": YES_NO
        }, {
            "label": "2. Has the software been developed in accordance with known standards?",
            "name": "Software[DevelopedToStandards]",
            "type": "dropdown",
            "divClass": "md:col-span-3",
            "dropdownData": YES_NO,
            show({ liveInputData }) {
                return liveInputData["Software[ContainsSoftware]"] === "Yes";
            }
        }, {
            "label": "3. What standard has been used to develop the software?",
            "name": "Software[Standard]",
            "type": "text",
            "divClass": "md:col-span-3",
            show({ liveInputData }) {
                return liveInputData["Software[ContainsSoftware]"] === "Yes";
            }
        }, {
            "label": "4. What is the Development Assurance Level (DAL) of the software?",
            "name": "Software[DAL]",
            "type": "text",
            "divClass": "md:col-span-3",
            show({ liveInputData }) {
                return liveInputData["Software[ContainsSoftware]"] === "Yes";
            }
        }, {
            "label": "5. Is the DAL level A-D?",
            "name": "Software[DALIsAToD]",
            "type": "dropdown",
            "divClass": "md:col-span-3",
            "dropdownData": YES_NO,
            show({ liveInputData }) {
                return liveInputData["Software[ContainsSoftware]"] === "Yes";
            }
        }, {
            "label": "6. Record the Software Accomplishment Summary reference, including revision level.",
            "name": "Software[AccomplishmentSummaryRef]",
            "type": "text",
            "divClass": "md:col-span-3",
            show({ liveInputData }) {
                return liveInputData["Software[ContainsSoftware]"] === "Yes" && liveInputData["Software[DALIsAToD]"] === "Yes";
            }
        }, {
            "label": "7. Are there any Open Problem Reports?",
            "name": "Software[OpenProblemReports]",
            "type": "dropdown",
            "divClass": "md:col-span-3",
            "dropdownData": YES_NO,
            show({ liveInputData }) {
                return liveInputData["Software[ContainsSoftware]"] === "Yes" && liveInputData["Software[DALIsAToD]"] === "Yes";
            }
        }, {
            "label": "⚠ Open Problem Reports will be flagged in the Equipment Limitations section.",
            "type": "sectionHeader",
            "divClass": "md:col-span-3",
            show({ liveInputData }) {
                return liveInputData["Software[OpenProblemReports]"] === "Yes";
            }
        }, {
            "label": "8. Is the equipment considered Avionic Equipment?",
            "name": "Software[IsAvionicEquipment]",
            "type": "dropdown",
            "divClass": "md:col-span-3",
            "dropdownData": YES_NO,
            show({ liveInputData }) {
                return liveInputData["Software[ContainsSoftware]"] === "Yes" && liveInputData["Software[DALIsAToD]"] === "Yes";
            }
        }, {
            "label": "9. Does the equipment have an (E)TSO?",
            "name": "Software[HasETSO]",
            "type": "dropdown",
            "divClass": "md:col-span-3",
            "dropdownData": YES_NO,
            show({ liveInputData }) {
                return liveInputData["Software[ContainsSoftware]"] === "Yes" && liveInputData["Software[DALIsAToD]"] === "Yes" && liveInputData["Software[IsAvionicEquipment]"] === "Yes";
            }
        }],
        async preSubmitHook({ liveInputData }) {
            const result = {};
            if (liveInputData["Software[ContainsSoftware]"] === "No") {
                result["Software"] = {
                    "ContainsSoftware": "No",
                    "DevelopedToStandards": "N/A",
                    "Standard": "N/A",
                    "DAL": "N/A",
                    "DALIsAToD": "N/A",
                    "AccomplishmentSummaryRef": "N/A",
                    "OpenProblemReports": "N/A",
                    "IsAvionicEquipment": "N/A",
                    "HasETSO": "N/A"
                };
            } else if (liveInputData["Software[DALIsAToD]"] === "No") {
                result["Software"] = {
                    "ContainsSoftware": "Yes",
                    "DevelopedToStandards": liveInputData["Software[DevelopedToStandards]"],
                    "Standard": liveInputData["Software[Standard]"],
                    "DAL": liveInputData["Software[DAL]"],
                    "DALIsAToD": "No",
                    "AccomplishmentSummaryRef": "N/A",
                    "OpenProblemReports": "N/A",
                    "IsAvionicEquipment": "N/A",
                    "HasETSO": "N/A"
                };
            } else if (liveInputData["Software[IsAvionicEquipment]"] === "No") {
                result["Software"] = {
                    "ContainsSoftware": "Yes",
                    "DevelopedToStandards": liveInputData["Software[DevelopedToStandards]"],
                    "Standard": liveInputData["Software[Standard]"],
                    "DAL": liveInputData["Software[DAL]"],
                    "DALIsAToD": "Yes",
                    "AccomplishmentSummaryRef": liveInputData["Software[AccomplishmentSummaryRef]"],
                    "OpenProblemReports": liveInputData["Software[OpenProblemReports]"],
                    "IsAvionicEquipment": "No",
                    "HasETSO": "N/A"
                };
            }
            return result;
        }
    });
    form.getDropdownData();
    form.toggle();
};

export const editAEHAssessment = ({ item, form, meta, pageItem }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? item?.ParentType ?? pageItem?.ParentType);
    const eadtCollection = collection(variant, "EADTs");
    form.setValues(item);
    form.setConfig({
        "title": "Edit AEH Assessment",
        "location": `${eadtCollection}/${item.ID}/`,
        "type": "update",
        "fields": [{
            "label": "Equipment AEH Assessment",
            "type": "sectionHeader",
            "divClass": "md:col-span-3"
        }, {
            "label": "1. Does the equipment contain AEH?",
            "name": "AEH[ContainsAEH]",
            "type": "dropdown",
            "divClass": "md:col-span-3",
            "dropdownData": YES_NO
        }, {
            "label": "2. Can the AEH be classified as simple?",
            "name": "AEH[IsSimple]",
            "type": "dropdown",
            "divClass": "md:col-span-3",
            "dropdownData": YES_NO,
            show({ liveInputData }) {
                return liveInputData["AEH[ContainsAEH]"] === "Yes";
            }
        }, {
            "label": "3. If the AEH is simple, provide details of how this has been determined.",
            "name": "AEH[SimpleDetails]",
            "type": "text",
            "divClass": "md:col-span-3",
            show({ liveInputData }) {
                return liveInputData["AEH[ContainsAEH]"] === "Yes" && liveInputData["AEH[IsSimple]"] === "Yes";
            }
        }, {
            "label": "4. If the AEH is not simple, has it been classified as Complex Electronic Hardware (CEH)?",
            "name": "AEH[IsCEH]",
            "type": "dropdown",
            "divClass": "md:col-span-3",
            "dropdownData": YES_NO,
            show({ liveInputData }) {
                return liveInputData["AEH[ContainsAEH]"] === "Yes" && liveInputData["AEH[IsSimple]"] === "No";
            }
        }, {
            "label": "5. If not CEH, provide details.",
            "name": "AEH[CEHDetails]",
            "type": "text",
            "divClass": "md:col-span-3",
            show({ liveInputData }) {
                return liveInputData["AEH[ContainsAEH]"] === "Yes" && liveInputData["AEH[IsSimple]"] === "No" && liveInputData["AEH[IsCEH]"] === "No";
            }
        }, {
            "label": "6. Has the CEH been developed in accordance with known standards?",
            "name": "AEH[CEHDevelopedToStandards]",
            "type": "dropdown",
            "divClass": "md:col-span-3",
            "dropdownData": YES_NO,
            show({ liveInputData }) {
                return liveInputData["AEH[ContainsAEH]"] === "Yes" && liveInputData["AEH[IsSimple]"] === "No" && liveInputData["AEH[IsCEH]"] === "Yes";
            }
        }, {
            "label": "7. What standard has been used to develop the CEH?",
            "name": "AEH[CEHStandard]",
            "type": "text",
            "divClass": "md:col-span-3",
            show({ liveInputData }) {
                return liveInputData["AEH[ContainsAEH]"] === "Yes" && liveInputData["AEH[IsSimple]"] === "No" && liveInputData["AEH[IsCEH]"] === "Yes";
            }
        }, {
            "label": "8. What is the Development Assurance Level (DAL) of the CEH?",
            "name": "AEH[CEHDAL]",
            "type": "text",
            "divClass": "md:col-span-3",
            show({ liveInputData }) {
                return liveInputData["AEH[ContainsAEH]"] === "Yes" && liveInputData["AEH[IsSimple]"] === "No" && liveInputData["AEH[IsCEH]"] === "Yes";
            }
        }, {
            "label": "9. Is the DAL level A-D?",
            "name": "AEH[CEHDALIsAToD]",
            "type": "dropdown",
            "divClass": "md:col-span-3",
            "dropdownData": YES_NO,
            show({ liveInputData }) {
                return liveInputData["AEH[ContainsAEH]"] === "Yes" && liveInputData["AEH[IsSimple]"] === "No" && liveInputData["AEH[IsCEH]"] === "Yes";
            }
        }, {
            "label": "10. Record the CEH Accomplishment Summary reference, including revision level.",
            "name": "AEH[CEHAccomplishmentSummaryRef]",
            "type": "text",
            "divClass": "md:col-span-3",
            show({ liveInputData }) {
                return liveInputData["AEH[ContainsAEH]"] === "Yes" && liveInputData["AEH[IsSimple]"] === "No" && liveInputData["AEH[IsCEH]"] === "Yes" && liveInputData["AEH[CEHDALIsAToD]"] === "Yes";
            }
        }, {
            "label": "11. Are there any Open Problem Reports?",
            "name": "AEH[CEHOpenProblemReports]",
            "type": "dropdown",
            "divClass": "md:col-span-3",
            "dropdownData": YES_NO,
            show({ liveInputData }) {
                return liveInputData["AEH[ContainsAEH]"] === "Yes" && liveInputData["AEH[IsSimple]"] === "No" && liveInputData["AEH[IsCEH]"] === "Yes" && liveInputData["AEH[CEHDALIsAToD]"] === "Yes";
            }
        }, {
            "label": "⚠ Open Problem Reports will be flagged in the Equipment Limitations section.",
            "type": "sectionHeader",
            "divClass": "md:col-span-3",
            show({ liveInputData }) {
                return liveInputData["AEH[CEHOpenProblemReports]"] === "Yes";
            }
        }, {
            "label": "12. Is the equipment considered Avionic Equipment?",
            "name": "AEH[IsAvionicEquipment]",
            "type": "dropdown",
            "divClass": "md:col-span-3",
            "dropdownData": YES_NO,
            show({ liveInputData }) {
                return liveInputData["AEH[ContainsAEH]"] === "Yes" && liveInputData["AEH[IsSimple]"] === "No" && liveInputData["AEH[IsCEH]"] === "Yes" && liveInputData["AEH[CEHDALIsAToD]"] === "Yes";
            }
        }, {
            "label": "13. Does the equipment have an (E)TSO?",
            "name": "AEH[HasETSO]",
            "type": "dropdown",
            "divClass": "md:col-span-3",
            "dropdownData": YES_NO,
            show({ liveInputData }) {
                return liveInputData["AEH[ContainsAEH]"] === "Yes" && liveInputData["AEH[IsSimple]"] === "No" && liveInputData["AEH[IsCEH]"] === "Yes" && liveInputData["AEH[CEHDALIsAToD]"] === "Yes" && liveInputData["AEH[IsAvionicEquipment]"] === "Yes";
            }
        }],
        async preSubmitHook({ liveInputData }) {
            const NA_ALL = {
                "ContainsAEH": "No", "IsSimple": "N/A", "SimpleDetails": "N/A",
                "IsCEH": "N/A", "CEHDetails": "N/A", "CEHDevelopedToStandards": "N/A",
                "CEHStandard": "N/A", "CEHDAL": "N/A", "CEHDALIsAToD": "N/A",
                "CEHAccomplishmentSummaryRef": "N/A", "CEHOpenProblemReports": "N/A",
                "IsAvionicEquipment": "N/A", "HasETSO": "N/A"
            };
            if (liveInputData["AEH[ContainsAEH]"] === "No") {
                return { "AEH": NA_ALL };
            }
            if (liveInputData["AEH[IsSimple]"] === "Yes") {
                return { "AEH": { ...NA_ALL, "ContainsAEH": "Yes", "IsSimple": "Yes", "SimpleDetails": liveInputData["AEH[SimpleDetails]"] } };
            }
            if (liveInputData["AEH[IsCEH]"] === "No") {
                return { "AEH": { ...NA_ALL, "ContainsAEH": "Yes", "IsSimple": "No", "IsCEH": "No", "CEHDetails": liveInputData["AEH[CEHDetails]"] } };
            }
            // CEH path — handle DAL and Avionic gatekeepers
            const result = {};
            if (liveInputData["AEH[CEHDALIsAToD]"] === "No") {
                result["AEH"] = {
                    "ContainsAEH": "Yes", "IsSimple": "No", "IsCEH": "Yes",
                    "CEHDevelopedToStandards": liveInputData["AEH[CEHDevelopedToStandards]"],
                    "CEHStandard": liveInputData["AEH[CEHStandard]"],
                    "CEHDAL": liveInputData["AEH[CEHDAL]"],
                    "CEHDALIsAToD": "No",
                    "CEHAccomplishmentSummaryRef": "N/A", "CEHOpenProblemReports": "N/A",
                    "IsAvionicEquipment": "N/A", "HasETSO": "N/A"
                };
            } else if (liveInputData["AEH[IsAvionicEquipment]"] === "No") {
                result["AEH"] = {
                    "ContainsAEH": "Yes", "IsSimple": "No", "IsCEH": "Yes",
                    "CEHDevelopedToStandards": liveInputData["AEH[CEHDevelopedToStandards]"],
                    "CEHStandard": liveInputData["AEH[CEHStandard]"],
                    "CEHDAL": liveInputData["AEH[CEHDAL]"],
                    "CEHDALIsAToD": "Yes",
                    "CEHAccomplishmentSummaryRef": liveInputData["AEH[CEHAccomplishmentSummaryRef]"],
                    "CEHOpenProblemReports": liveInputData["AEH[CEHOpenProblemReports]"],
                    "IsAvionicEquipment": "No", "HasETSO": "N/A"
                };
            }
            return result;
        }
    });
    form.toggle();
};

// --- Section C: Final Section (Detailed EADT) ---

export const editFinalSection = ({ item, form, meta, pageItem }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? item?.ParentType ?? pageItem?.ParentType);
    const eadtCollection = collection(variant, "EADTs");
    form.setValues(item);
    form.setConfig({
        "title": "Edit Additional Data",
        "location": `${eadtCollection}/${item.ID}/`,
        "type": "update",
        "fields": [{
            "label": "Additional Data",
            "name": "AdditionalData",
            "divClass": "md:col-span-3",
            "type": "richtext"
        }, {
            "label": "Required Maintenance",
            "name": "RequiredMaintenance",
            "divClass": "md:col-span-3",
            "type": "richtext"
        }, {
            "label": "Suitability for Proposed Application",
            "name": "Suitability",
            "divClass": "md:col-span-3",
            "type": "richtext"
        }]
    });
    form.toggle();
};

// --- Equipment Limitations (shared by Simplified & Detailed) ---

export const editLimitations = ({ item, form, meta, pageItem }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? item?.ParentType ?? pageItem?.ParentType);
    const eadtCollection = collection(variant, "EADTs");
    // Auto-detect OPR flags from Software and AEH assessments
    const autoLimitations = [];
    if (item.Software?.OpenProblemReports === "Yes") {
        autoLimitations.push("Software: Open Problem Reports identified — see Software Assessment");
    }
    if (item.AEH?.CEHOpenProblemReports === "Yes") {
        autoLimitations.push("AEH: Open Problem Reports identified — see AEH Assessment");
    }

    const values = { ...item };
    if (autoLimitations.length > 0 && !item.LimitationL1) values.LimitationL1 = autoLimitations[0] || "";
    if (autoLimitations.length > 1 && !item.LimitationL2) values.LimitationL2 = autoLimitations[1] || "";

    form.setValues(values);
    form.setConfig({
        "title": "Edit Equipment Limitations",
        "location": `${eadtCollection}/${item.ID}/`,
        "type": "update",
        "fields": [{
            "label": "⚠ Detected flagged findings from assessments — pre-populated in limitations below.",
            "type": "sectionHeader",
            "divClass": "md:col-span-3",
            show() {
                return autoLimitations.length > 0;
            }
        }, {
            "label": "L1",
            "name": "LimitationL1",
            "type": "text",
            "divClass": "md:col-span-3",
            "required": false
        }, {
            "label": "L2",
            "name": "LimitationL2",
            "type": "text",
            "divClass": "md:col-span-3",
            "required": false
        }, {
            "label": "L3",
            "name": "LimitationL3",
            "type": "text",
            "divClass": "md:col-span-3",
            "required": false
        }]
    });
    form.toggle();
};

// --- Environmental Qualification Attachable (Detailed EADT) ---

export const envQualAttachable = {
    "label": "Environmental Qualification",
    "key": "EnvQualifications",
    "local": true,
    "headers": [{
        "headerName": "Condition",
        "field": "Condition"
    }, {
        "headerName": "Standard",
        "field": "Standard"
    }, {
        "headerName": "Section",
        "field": "Section"
    }, {
        "headerName": "Description of Tests Conducted",
        "field": "Description"
    }],
    "actions": [{
        "label": "Add Environmental Qualification",
        action({ form, pageItem, meta }) {
            const variant = getVariantByParentType(meta?.ParentType ?? pageItem?.ParentType);
            const eadtCollection = collection(variant, "EADTs");
            form.setConfig({
                "title": "Add Environmental Qualification",
                "type": "push",
                "location": `${eadtCollection}/${pageItem.ID}/EnvQualifications`,
                "fields": [{
                    "label": "Condition",
                    "name": "Condition",
                    "type": "dropdown",
                    "divClass": "md:col-span-3",
                    "dropdownData": ENV_CONDITIONS
                }, {
                    "label": "Standard",
                    "name": "Standard",
                    "type": "text",
                    "divClass": "md:col-span-3"
                }, {
                    "label": "Section",
                    "name": "Section",
                    "type": "text",
                    "divClass": "md:col-span-3"
                }, {
                    "label": "Description of Tests Conducted",
                    "name": "Description",
                    "type": "text",
                    "divClass": "md:col-span-3"
                }],
                preSubmitHook({ liveInputData }) {
                    const cond = ENV_CONDITIONS.find(c => c.id === liveInputData.Condition);
                    return { "Condition": cond ? cond.label : liveInputData.Condition };
                }
            });
            form.getDropdownData();
            form.toggle();
        }
    }],
    "rowActions": [{
        "label": "Edit",
        action({ form, pageItem, rowItem, meta }) {
            const variant = getVariantByParentType(meta?.ParentType ?? pageItem?.ParentType ?? rowItem?.ParentType);
            const eadtCollection = collection(variant, "EADTs");
            form.setValues(rowItem);
            form.setConfig({
                "title": "Edit Environmental Qualification",
                "type": "update",
                "location": `${eadtCollection}/${pageItem.ID}/EnvQualifications/${rowItem.ID}`,
                "fields": [{
                    "label": "Condition",
                    "name": "Condition",
                    "type": "text",
                    "divClass": "md:col-span-3"
                }, {
                    "label": "Standard",
                    "name": "Standard",
                    "type": "text",
                    "divClass": "md:col-span-3"
                }, {
                    "label": "Section",
                    "name": "Section",
                    "type": "text",
                    "divClass": "md:col-span-3"
                }, {
                    "label": "Description of Tests Conducted",
                    "name": "Description",
                    "type": "text",
                    "divClass": "md:col-span-3"
                }]
            });
            form.toggle();
        }
    }, {
        "label": "Remove",
        "class": "text-red",
        action({ form, pageItem, rowItem, meta }) {
            const variant = getVariantByParentType(meta?.ParentType ?? pageItem?.ParentType ?? rowItem?.ParentType);
            const eadtCollection = collection(variant, "EADTs");
            form.setConfig({
                "title": "Remove Environmental Qualification",
                "description": `Are you sure you want to remove <strong>${rowItem.Condition}</strong>?`,
                "type": "delete",
                "location": `${eadtCollection}/${pageItem.ID}/EnvQualifications/${rowItem.ID}`,
                "fields": []
            });
            form.toggle();
        }
    }]
};

export const completeEadt = ({ item, db, form, meta, pageItem }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? item?.ParentType ?? pageItem?.ParentType);
    const eadtCollection = collection(variant, "EADTs");
    form.setValues(item);
    form.setConfig({
        "title": "Complete EADT",
        "location": `${eadtCollection}/${item.ID}/`,
        "description": "Are you sure you want to complete this EADT?",
        "type": "update",
        "fields": [],
        async preSubmitHook({ files }) {
            return {
                "Completed": true,
                "Date": {
                    "Completed": new Date().toISOString()
                }
            }
        }
    });
    form.toggle();
};

export const reviseEadt = ({ item, db, form, store, meta, pageItem }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? item?.ParentType ?? pageItem?.ParentType);
    const eadtCollection = collection(variant, "EADTs");
    const currentRevisionID = item.RevisionID;

    const oldDataToSave = { ...item };
    const fieldsToOmit = ["ID", "RevisionID", "Revisions", "Date", "Completed", "Cancelled", "Links", "key", "Type", "Number", "Title", "ExpectedTime", "TypeID"];
    fieldsToOmit.forEach(f => delete oldDataToSave[f]);

    const nextRevisionNumber = (parseInt(item.Revision) || 0) + 1;
    const userID = store.state.user.ID;
    
    form.setValues(item);
    form.setConfig({
        "title": "Revise EADT",
        "location": `${eadtCollection}/${item.ID}/Revisions`,
        "description": `You are creating a revision of this EADT. Please provide a reason for the revision and acknowledge any revision guidance provided. Revision from <strong>${item.Revision || "N/A"}</strong> to <strong>${nextRevisionNumber}</strong>.`,
        "type": "push",
        "fields": [{
            "label": "Reason for Revision",
            "name": "RevisionReason",
            "type": "text",
            "divClass": "md:col-span-3"
        }, {
            "label": "SOME PROMPT TO ACKNOWLEDGE REVISION GUIDANCE",
            "type": "checkbox",
            "name": "RevisionGuidanceAcknowledged",
            "divClass": "md:col-span-3"
        }],
         preSubmitHook() {
            
            return {
                "Date": new Date().toISOString(),
                "By": userID,
                "Revision": nextRevisionNumber
            }
        },
        async postSubmitHook({ newData }) {
            await db.update(`${eadtCollection}/${item.ID}`, {
                "RevisionID": newData.ID,
                "Revision": newData.Revision,
                "Completed": false
            });

            await db.update(`${eadtCollection}/${item.ID}/Revisions/${currentRevisionID}`, {
                "PreviousEADTData": oldDataToSave,
            });
        }
    });
    form.toggle();
}

export const linkEadtToEAR = ({ pageItem, form, meta }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? pageItem?.ParentType);
    const eadtCollection = collection(variant, "EADTs");
    const earCollection = collection(variant, "EARs");
    form.setConfig({
        "title": "Link EADT to EAR",
        "location": `${earCollection}/${pageItem.ID}/EADTs/`,
        "type": "update",
        "fields": [{
            "label": "EADT",
            "name": "_doc",
            "type": "dropdown",
            "dataLocation": eadtCollection,
            "labelKey": "Number",
            "idKey": "ID",
            "search": true
        }],
        preSubmitHook({ selectedDropdowns }) {
            const selectedEADT = selectedDropdowns._doc;
            
            return {
                [selectedEADT.id]: true,
                "_doc": null
            };
        },
        async postSubmitHook({ liveInputData, selectedDropdowns, db }) {
            const selectedEADT = selectedDropdowns._doc.meta;
            await db.set(`${eadtCollection}/${selectedEADT.ID}/Links/${pageItem.ID}`, selectedEADT.RevisionID);
        }
    });
    form.getDropdownData();
    form.toggle();
}

export const changeLinkedRevision = ({ form, pageItem, rowItem, meta }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? pageItem?.ParentType ?? rowItem?.ParentType);
    const eadtCollection = collection(variant, "EADTs");
    form.setConfig({
        "title": "Change linked revision",
        "type": "update",
        "location": `${eadtCollection}/${rowItem.ID}/Links/`,
        "fields": [{
            "label": "Revision",
            "name": pageItem.ID,
            "type": "dropdown",
            dropdownData: Object.entries(rowItem.Revisions).map(([key, value]) => ({ id: key, label: value.Revision }))
        }]
    });
    form.getDropdownData();
    form.toggle();
};
